import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
  dubaiHotels,
  dubaiTours,
  dubaiTransfers,
  dubaiVisa,
} from "../data/data";
import { Star, Clock, Users, CheckCircle } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const Cyprus = () => {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4 text-center">
            {t("cyprusSpecial")}
          </h1>
          <p className="text-xl text-gray-300 text-center max-w-2xl mx-auto">
            {t("cyprusSubTitle")}
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <Tabs defaultValue="hotels" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-12 bg-white shadow-md rounded-xl p-2">
            <TabsTrigger
              value="hotels"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white rounded-lg font-semibold transition-all duration-300"
            >
              {t("cyprusHotels")}
            </TabsTrigger>
            <TabsTrigger
              value="tours"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white rounded-lg font-semibold transition-all duration-300"
            >
              {t("cyprusTours")}
            </TabsTrigger>
            <TabsTrigger
              value="transfer"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white rounded-lg font-semibold transition-all duration-300"
            >
              {t("cyprusTransfer")}
            </TabsTrigger>
            <TabsTrigger
              value="visa"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white rounded-lg font-semibold transition-all duration-300"
            >
              {t("cyprusVisa")}
            </TabsTrigger>
          </TabsList>

          {/* Hotels Tab */}
          <TabsContent value="hotels">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dubaiHotels.map((hotel) => (
                <a
                  key={hotel.id}
                  href={hotel.website || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={hotel.image}
                      alt={hotel.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center space-x-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold text-gray-900">
                        {hotel.stars}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {hotel.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {hotel.description[language]}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </TabsContent>

          {/* Tours Tab */}
          <TabsContent value="tours">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dubaiTours.map((tour) => (
                <div
                  key={tour.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  {/* Görsel */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={tour.image || "/images/placeholder.jpg"}
                      alt={tour.name[language]}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-800 shadow-sm">
                      {tour.duration}
                    </div>
                  </div>

                  {/* İçerik */}
                  <div className="p-6 flex flex-col justify-between h-full">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {tour.name[language]}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {tour.description[language]}
                      </p>
                    </div>

                    {/* Fiyat alanı */}
                    <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-3xl font-bold text-amber-600">
                        ${tour.price}
                      </span>
                      <span className="text-sm text-gray-500">
                        / {language === "tr" ? "kişi" : "person"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Transfer Tab */}
          <TabsContent value="transfer">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dubaiTransfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={transfer.image}
                      alt={transfer.name[language]}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {transfer.name[language]}
                    </h3>
                    <div className="flex items-center space-x-2 text-gray-500 mb-4">
                      <Users className="h-4 w-4" />
                      <span className="text-sm">
                        {language === "tr" ? "Kapasite" : "Capacity"}:{" "}
                        {transfer.capacity}{" "}
                        {language === "tr" ? "kişi" : "people"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div>
                        <span className="text-3xl font-bold text-amber-600">
                          ${transfer.price}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Visa Tab */}
          <TabsContent value="visa">
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  {t("cyprusVisa")}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-6 bg-amber-50 rounded-xl">
                    <div className="text-4xl font-bold text-amber-600 mb-2">
                      {dubaiVisa.price[language]}
                    </div>
                    <div className="text-gray-600">
                      {language === "tr" ? "Vize Ücreti" : "Visa Fee"}
                    </div>
                  </div>
                  <div className="text-center p-6 bg-amber-50 rounded-xl">
                    <div className="text-4xl font-bold text-amber-600 mb-2">
                      {dubaiVisa.duration[language]}
                    </div>
                    <div className="text-gray-600">
                      {language === "tr" ? "Vize Süresi" : "Visa Duration"}
                    </div>
                  </div>
                  <div className="text-center p-6 bg-amber-50 rounded-xl">
                    <div className="text-4xl font-bold text-amber-600 mb-2">
                      {dubaiVisa.processing[language]}
                    </div>
                    <div className="text-gray-600">
                      {language === "tr" ? "İşlem Süresi" : "Processing Time"}
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {language === "tr"
                      ? "Gerekli Belgeler"
                      : "Required Documents"}
                  </h3>
                  <ul className="space-y-3">
                    {dubaiVisa.requirements[language].map((req, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 text-center">
                  <Link
                    to="/contact"
                    className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    {language === "tr"
                      ? "Vize Başvurusu Yap"
                      : "Apply for Visa"}
                  </Link>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Cyprus;
